import type { DashboardComponent, DashboardDefinition } from 'common/types/dashboard/dashboardDefinition';
import { getValueType } from 'common/types/summaryDefinitions';
import type { ValueType } from 'common/types/summaryDefinitions/valueTypes/valueType';
import {
  getDisplayFormatTypeForColumn,
  isOpValidForFormatType,
  validateFilterValue,
} from 'pages/Firms/pages/FirmSummary/filterValidation';
import { resolveColumnLabel } from 'pages/Intelligence/SummaryDashboard/components/DashboardGrid/dashboardComponent.helpers';
import {
  MAX_GRID_COLUMNS,
  MAX_LIST_LIMIT,
  MAX_TAGS,
  MIN_GRID_COLUMNS,
  MIN_LIST_LIMIT,
  readGridColumns,
  readTableSpreadsheet,
} from './dashboardDraft';
import { emptyDashboardIssues } from './dashboardEditFields.types';
import type { DashboardIssues } from './dashboardEditFields.types';

interface ValidateDraftParams {
  definition: DashboardDefinition;
  // Names of the other definitions in the tab strip, for the duplicate check.
  existingNames: readonly string[];
  name: string;
}

const normalizeName = (name: string) => name.trim().toLowerCase();

const isPositiveInteger = (value: number | undefined): value is number =>
  typeof value === 'number' && Number.isInteger(value) && value >= 1;

// The backend validates neither filter operators nor filter values — _validate_embedded_spreadsheet
// checks only the entry's arity and its column. So for filters the client is the only guard there is.
const validateFilters = (
  component: Extract<DashboardComponent, { type: 'table' }>,
  columns: readonly string[],
  push: (message: string) => void
): void => {
  const { filters } = readTableSpreadsheet(component.fields);
  const valueTypeById = new Map<string, ValueType>(
    columns
      .map(column => [column, getValueType(column)] as const)
      .filter((entry): entry is readonly [string, ValueType] => Boolean(entry[1]))
  );

  filters.forEach(([column, [operator, value]]) => {
    const label = resolveColumnLabel(column);

    if (!columns.includes(column)) {
      push(`Filter references "${label}", which is not one of the table's columns.`);
      return;
    }

    const formatType = getDisplayFormatTypeForColumn(column, valueTypeById);

    if (!isOpValidForFormatType(operator, formatType)) {
      push(`Filter on "${label}" uses a condition that does not apply to that column.`);
    }

    if (String(value ?? '').trim() === '') {
      push(`Filter on "${label}" needs a value.`);
      return;
    }

    const { error, valid } = validateFilterValue(value, formatType);
    if (!valid) {
      push(`Filter on "${label}": ${error ?? 'invalid value'}.`);
    }
  });
};

const validateComponent = (component: DashboardComponent, push: (message: string) => void): void => {
  if ((component.display?.tags?.length ?? 0) > MAX_TAGS) {
    // The backend truncates silently rather than erroring, so losing tag 11 without a word is worse
    // than a blocked save.
    push(`A component can carry at most ${MAX_TAGS} tags.`);
  }

  if (!isPositiveInteger(component.position?.w) || !isPositiveInteger(component.position?.h)) {
    push('Width and height must be whole numbers of 1 or more.');
  }

  switch (component.type) {
    case 'totals':
      if (!component.fields.metrics.length) {
        push('Pick at least one metric.');
      }
      return;
    case 'bar_chart':
    case 'line_chart':
      if (!component.fields.dimension) {
        push('Pick a dimension.');
      }
      if (!component.fields.metrics.length) {
        push('Pick at least one metric.');
      }
      return;
    case 'pie_chart':
      if (!component.fields.dimension) {
        push('Pick a dimension.');
      }
      if (!component.fields.metric) {
        push('Pick a metric.');
      }
      return;
    case 'table': {
      const { columns, sortKeys } = readTableSpreadsheet(component.fields);

      if (!columns.length) {
        push('Pick at least one column.');
      }

      sortKeys.forEach(([column]) => {
        if (!columns.includes(column)) {
          push(`Sort references "${resolveColumnLabel(column)}", which is not one of the table's columns.`);
        }
      });

      validateFilters(component, columns, push);
      return;
    }
    case 'content_card':
      return;
    case 'list': {
      const { entity_kind: entityKind, limit, metric } = component.fields;

      if (!metric) {
        push('Pick a metric.');
      }
      if (!entityKind) {
        push('Pick what the list ranks over.');
      }
      if (limit !== undefined && (!Number.isInteger(limit) || limit < MIN_LIST_LIMIT || limit > MAX_LIST_LIMIT)) {
        push(`Limit must be a whole number between ${MIN_LIST_LIMIT} and ${MAX_LIST_LIMIT}.`);
      }
      return;
    }
    default: {
      // Exhaustive by construction — a new backend component type stops this compiling until it
      // gets its own required-field rules.
      const unhandled: never = component;
      return unhandled;
    }
  }
};

// Everything checked here is something the user can trip with the controls on screen. Vocabulary
// membership is deliberately absent: the pickers are built from the generated arrays, so an unknown
// metric is unreachable, and the backend stays the authority for hand-edited or legacy rows.
export const validateDraft = ({ definition, existingNames, name }: ValidateDraftParams): DashboardIssues => {
  const issues = emptyDashboardIssues();
  const trimmedName = name.trim();

  if (!trimmedName) {
    issues.general.push('Name is required.');
  } else if (existingNames.some(existing => normalizeName(existing) === normalizeName(trimmedName))) {
    issues.general.push('A summary with this name already exists.');
  }

  const columns = readGridColumns(definition);
  if (!Number.isInteger(columns) || columns < MIN_GRID_COLUMNS || columns > MAX_GRID_COLUMNS) {
    issues.general.push(`Grid columns must be a whole number between ${MIN_GRID_COLUMNS} and ${MAX_GRID_COLUMNS}.`);
  }

  definition.components.forEach((component, index) => {
    const messages: string[] = [];
    validateComponent(component, message => messages.push(message));

    if (messages.length) {
      issues.byComponentIndex.set(index, messages);
    }
  });

  return issues;
};
